import React from "react"
import Box from "@mui/material/Box";
import CCOHeadline from "../../fundamentals/CCOHeadline";
import {CmCollectionType, CmTeasableType, SosDonationProductType, SosTeaserType} from "../../types";
import TeaserCard from "./TeaserCard";
import CCOContainer from "../../fundamentals/CCOContainer";
import getSetting, {
    SETTING_GALLERY_AUTOPLAY,
    SETTING_GALLERY_AUTOPLAY_SPEED,
    SETTING_GALLERY_IMAGESLIDE_SPEED
} from "../../../pages/config/settings";
import CCOComponentWrapper from "../../fundamentals/CCOComponentWrapper";
import {getKey} from "../../../utility/componentUtil";
import useWindowSize from "../../../utility/useWindowSize";
import ProductTeaserCard from "../C085ProductTeaser/ProductTeaserCard";
import dynamic from "next/dynamic";


export interface Props {
    cmCollection: CmCollectionType
    headline?: string
    background?: string
    isHeroCarousel?: boolean
    isSmall?: boolean
    renderInline?: boolean
    minHeight?: string | number
}

export function getContentClassName(id: string) {
    return `carousel-content-${id}`;
}

const CCOCarousel = dynamic(() => import('../../fundamentals/CCOCarousel'), {
    ssr: false
})


function C020TeaserCarousel(props: Props) {
    const carouselContentClassName = getContentClassName(getKey(props?.cmCollection?.id));
    const {lowerThanWindowMedium} = useWindowSize()
    const items = props.cmCollection.items ?? [];

    if (items.length === 0) {
        return null;
    }

    function prepareItems(collectionItems: Array<any>) {
        const preparedItems: Array<any> = [];
        collectionItems.map((item: any, index: number) => {
            preparedItems.push(
                <Box
                    className={carouselContentClassName}
                    sx={{minWidth: "100%"}}
                    data-cypress={`carousel-item-${index}`}>
                    {
                        item.type === 'SOSDonationProduct' ?
                            <ProductTeaserCard teasercardLocation='CO20 Teaser-Karussell Produkte' item={item}/> :
                            <TeaserCard item={item} teasercardLocation={props.isHeroCarousel ? 'Hero TeaserCarousel (CO20)' : 'CO20 Teaser-Karussell'}
                                        hasPriority={props.isHeroCarousel && index === 0}
                                        showAiGeneratedImageLabel/>
                    }
                </Box>
            );
        });
        return preparedItems;
    }

    const isProduct = (items[0] as CmTeasableType | undefined)?.type === 'SOSDonationProduct';

    function renderCarousel() {
        return <CCOCarousel
            autoPlay={getSetting((props.cmCollection as any).teaserCarouselSettings,
                SETTING_GALLERY_AUTOPLAY)}
            interval={getSetting((props.cmCollection as any).teaserCarouselSettings,
                SETTING_GALLERY_AUTOPLAY_SPEED)}
            duration={getSetting((props.cmCollection as any).teaserCarouselSettings,
                SETTING_GALLERY_IMAGESLIDE_SPEED)}
            items={prepareItems(items)}
            paginateItems={true}
            contentClassName={carouselContentClassName}
            isHeroCarousel={props.isHeroCarousel}
            minHeight={props.minHeight}
        />;
    }

    function renderBox() {
        return <Box>
            {isProduct ?
                <ProductTeaserCard
                    item={items[0] as SosDonationProductType}
                    horizontal={!lowerThanWindowMedium}
                    teasercardLocation='CO20 Teaser-Karussell Produkte'
                /> :
                <TeaserCard
                    item={items[0] as SosTeaserType}
                    horizontal={!lowerThanWindowMedium}
                    teasercardLocation={props.isHeroCarousel ? 'C075 Stage-Teaser-Hero' : 'CO20 Teaser-Karussell'}
                    showAiGeneratedImageLabel
                />
            }
        </Box>;
    }

    function renderElements() {
        return <>
            {items.length > 1 ? renderCarousel() : renderBox()}
        </>;
    }

    const headline = <CCOHeadline id={props.cmCollection.id}
                                  metadataId={props.cmCollection.id}
                                  propertyName={props.headline ? 'properties.headline' :
                                      'properties.teaserTitle'}
                                  subtextPropertyName='properties.teaserText'
                                  headline={props.headline ? props.headline :
                                      props.cmCollection.teaserTitle}
                                  type={"componenthead"}
                                  subtext={props.cmCollection.teaserText?.textAsTree}
                                  background={'grey'}
                                  isRichText={props.renderInline}
                                  suppressDivider={props.renderInline}
                                  suppressHeadlineMarginBottom={props.renderInline}/>

    if (props.renderInline) {
        return <>
            {headline}
            {renderElements()}
        </>;
    }

    return <CCOComponentWrapper
        background={'grey'}
        dataCypress='teaser-carousel'
        noBottomMargin={props.isHeroCarousel}
        noTopMargin={props.isHeroCarousel}
        {...(props.isHeroCarousel ? {customStyle: {paddingTop: 0}} : {})}
    >
        {headline}
        <CCOContainer background={'grey'} width={"large"}>
            {
                props.isSmall ?
                    <CCOComponentWrapper>
                        <CCOContainer width='large'>
                            <CCOContainer width='large'>
                                {renderElements()}
                            </CCOContainer>
                        </CCOContainer>
                    </CCOComponentWrapper> :
                    renderElements()
            }
        </CCOContainer>
    </CCOComponentWrapper>
}

export default C020TeaserCarousel
